import { jsx } from 'react/jsx-runtime';
import { createContext, useRef, useEffect, useState, useContext, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { loadVGSCollect } from '@vgs/collect-js';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var PaymentServiceContext = /*#__PURE__*/createContext(undefined);

var usePrevious = function usePrevious(value) {
  var ref = useRef(value);
  useEffect(function () {
    ref.current = value;
  }, [value]);
  return ref.current;
};

function handleError(error) {
  console.error('Payment service failed to load', error);
}

function parsePaymentServicePromise(promise) {
  return promise["catch"](handleError);
}

function PaymentServiceProvider(_ref) {
  var paymentService = _ref.paymentService,
      children = _ref.children;

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      form = _useState2[0],
      setForm = _useState2[1];

  var _useState3 = useState(),
      _useState4 = _slicedToArray(_useState3, 2),
      formState = _useState4[0],
      setFormState = _useState4[1];

  var prevPaymentServicePromise = usePrevious(paymentService);
  useEffect(function () {
    if (prevPaymentServicePromise !== null && prevPaymentServicePromise !== paymentService) {
      console.warn('Unsupported prop change on PaymentServiceProvider: You cannot change the `paymentService` prop after setting it.');
    }
  }, [prevPaymentServicePromise, paymentService]);
  useEffect(function initializeForm() {
    var isMounted = true;

    if (!form) {
      parsePaymentServicePromise(paymentService).then(function (resolvedPaymentService) {
        if (resolvedPaymentService && isMounted) {
          var newForm = resolvedPaymentService.init(function (state) {
            return setFormState(state);
          });
          setForm(newForm);
        }
      });
    }

    return function () {
      isMounted = false;
    };
  }, [paymentService, form]);
  return jsx(PaymentServiceContext.Provider, Object.assign({
    value: {
      form: form,
      formState: formState
    }
  }, {
    children: children
  }), void 0);
}

function usePaymentService() {
  var context = useContext(PaymentServiceContext);

  if (context === undefined) {
    throw new Error('usePaymentService must be used within a PaymentServiceProvider');
  }

  return context;
}

var _excluded = ["ariaLabel", "autoComplete", "className", "css", "disabled", "hideValue", "id", "placeholder", "readOnly", "showCardIcon", "type", "validations", "wrapperProps"];
function PaymentElement(_ref) {
  var ariaLabel = _ref.ariaLabel,
      autoComplete = _ref.autoComplete,
      className = _ref.className,
      css = _ref.css,
      _disabled = _ref.disabled,
      hideValue = _ref.hideValue,
      _id = _ref.id,
      placeholder = _ref.placeholder,
      _readOnly = _ref.readOnly,
      showCardIcon = _ref.showCardIcon,
      type = _ref.type,
      validations = _ref.validations,
      wrapperProps = _ref.wrapperProps,
      fieldOptions = _objectWithoutProperties(_ref, _excluded);

  var _usePaymentService = usePaymentService(),
      form = _usePaymentService.form;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isFieldDeleted = _useState2[0],
      setIsFieldDeleted = _useState2[1];

  var fieldRef = useRef(null);
  var id = _id || type;
  var disabled = _disabled ? 'disabled' : undefined;
  var readOnly = _readOnly ? 'readonly' : undefined;
  var fieldUpdatePayload = useMemo(function () {
    return {
      ariaLabel: ariaLabel,
      autoComplete: autoComplete,
      css: css,
      disabled: disabled,
      hideValue: hideValue,
      placeholder: placeholder,
      readOnly: readOnly,
      showCardIcon: showCardIcon,
      validations: validations
    };
  }, [ariaLabel, autoComplete, css, disabled, hideValue, placeholder, readOnly, showCardIcon, validations]);
  var fieldUpdatePayloadRef = useRef(fieldUpdatePayload);
  useEffect(function initializeField() {
    var field = form === null || form === void 0 ? void 0 : form.fields.find(function (_field) {
      return _field.name === id;
    });

    if (form && (!field || isFieldDeleted)) {
      form.field("#".concat(id), _objectSpread2(_objectSpread2({
        name: id,
        type: type,
        disabled: disabled,
        validations: validations,
        ariaLabel: ariaLabel,
        autoComplete: autoComplete,
        hideValue: hideValue,
        placeholder: placeholder,
        readOnly: readOnly,
        showCardIcon: showCardIcon
      }, fieldOptions), {}, {
        css: _objectSpread2({
          boxSizing: 'border-box',
          padding: '0 8px'
        }, css)
      }));
      setIsFieldDeleted(false);
    }
  }, [ariaLabel, autoComplete, css, disabled, fieldOptions, form, hideValue, id, isFieldDeleted, placeholder, readOnly, setIsFieldDeleted, showCardIcon, type, validations]);
  useEffect(function deleteField() {
    var _a;

    var field = form === null || form === void 0 ? void 0 : form.fields.find(function (_field) {
      return _field.name === id;
    });

    if (form && field && !((_a = fieldRef.current) === null || _a === void 0 ? void 0 : _a.children.length)) {
      field["delete"]();
      setIsFieldDeleted(true);
    }
  }, [form, id, setIsFieldDeleted]);
  useEffect(function updateOptions() {
    var field = form === null || form === void 0 ? void 0 : form.fields.find(function (_field) {
      return _field.name === id;
    });

    if (field && !isEqual(fieldUpdatePayload, fieldUpdatePayloadRef.current)) {
      fieldUpdatePayloadRef.current = fieldUpdatePayload;
      field.update(fieldUpdatePayload);
    }
  }, [form === null || form === void 0 ? void 0 : form.fields, hideValue, id, fieldUpdatePayload, placeholder]);
  return jsx("div", Object.assign({
    ref: fieldRef,
    className: className,
    id: id
  }, wrapperProps), void 0);
}

var VGS_COLLECT_VERSION = '2.15.0';

function loadPaymentService(config) {
  return loadVGSCollect(_objectSpread2({
    version: VGS_COLLECT_VERSION
  }, config));
}

export { PaymentElement, PaymentServiceProvider, loadPaymentService, usePaymentService };
